import { useCallback, useId, useMemo, useRef } from 'react';
import {
  ArrowDownRight,
  ArrowUpRight,
  Banknote,
  CheckCircle2,
  Hourglass,
  Minus,
  TicketCheck,
  Users,
} from 'lucide-react';
import styles from './KpiStrip.module.css';
import useCountUp from '../../../hooks/useCountUp';
import { fmtEUR, fmtInt } from '../../../utils/moneyFormat';

const TONE_STOPS = {
  Brand: ['#f5a51e', '#fbc963'],
  Pos: ['#16955a', '#34c07a'],
  Accent: ['#2563eb', '#60a5fa'],
  Warn: ['#ca8a04', '#eab308'],
};

// fmtEUR / fmtInt own the European formatting; this only slices their output so
// the magnitude can be set larger than its currency mark and its cents. It
// never reformats — it splits the exact string the formatter produced.
const splitFigure = (formatted) => {
  const s = String(formatted);
  const m = s.match(/^(\D*?)([\d.  ]*\d)(?:,(\d+))?(.*)$/);
  if (!m) return { pre: '', int: s, dec: '', post: '' };
  return { pre: (m[1] || '').trim(), int: m[2] || '', dec: m[3] || '', post: (m[4] || '').trim() };
};

// ── The instrument bay ─────────────────────────────────────────────────────
// The curve plots the running total, so its last point IS the headline figure
// above it — chart and number reconcile, which a daily sparkline can never
// promise. Beneath it: the day's own take as faint columns, a dashed pace line
// for the previous period, weekend banding and a quarter scale.
const Plot = ({ values, daily, dates, tone, pace, axisLabel, readRef, hitRef }) => {
  const gid = useId().replace(/:/g, '');
  const [from, to] = TONE_STOPS[tone] || TONE_STOPS.Accent;

  const geo = useMemo(() => {
    const v = (values || []).map((n) => Number(n) || 0);
    if (v.length < 2) return null;
    const W = 200;
    const H = 46;
    // The axis has to clear the pace line too, or a slow period would draw its
    // own benchmark straight through the ceiling of the plot.
    const top = Math.max(...v, Number(pace) || 0, 1);
    const step = W / (v.length - 1);
    const y = (n) => H - (n / top) * (H - 7) - 3;
    const pts = v.map((n, i) => [i * step, y(n)]);
    const line = pts
      .map(([x, yy], i) => `${i ? 'L' : 'M'}${x.toFixed(1)},${yy.toFixed(1)}`)
      .join(' ');
    const len = pts.reduce(
      (a, p, i) => (i ? a + Math.hypot(p[0] - pts[i - 1][0], p[1] - pts[i - 1][1]) : 0),
      0
    );

    const dv = (daily || []).map((n) => Number(n) || 0);
    const dTop = Math.max(...dv, 1);
    // Capped at 42% of the bay so the daily take reads as texture beneath the
    // curve rather than as a second chart competing with it.
    const cols = dv.map((n, i) => ({
      x: i * step,
      h: n > 0 ? Math.max(1.5, (n / dTop) * (H * 0.42)) : 0,
    }));

    // Weekend banding read off the YYYY-MM-DD prefix through Date.UTC — local
    // date getters shift the day by one on a UTC server.
    const bands = [];
    (dates || []).forEach((d, i) => {
      if (!d) return;
      const [yy, mm, dd] = String(d).slice(0, 10).split('-').map(Number);
      if (!yy) return;
      const wd = new Date(Date.UTC(yy, mm - 1, dd)).getUTCDay();
      if (wd === 0 || wd === 6) {
        bands.push({ x: Math.max(0, i * step - step / 2), w: step });
      }
    });

    return {
      W,
      H,
      line,
      area: `${line} L${W},${H} L0,${H} Z`,
      end: pts[pts.length - 1],
      len: Math.ceil(len) || 1,
      cols,
      bands,
      paceY: Number(pace) > 0 ? y(Number(pace)) : null,
      flat: v.every((n) => n <= 0),
    };
  }, [values, daily, dates, pace]);

  // The scrub writes straight to the DOM through refs. Driving this from React
  // state would re-reconcile every node in the plot on each pointermove.
  const onMove = useCallback(
    (e) => {
      const el = readRef?.current;
      const hit = hitRef?.current;
      if (!geo || !el || !hit) return;
      const r = hit.getBoundingClientRect();
      if (!r.width) return;
      const n = (values || []).length;
      const i = Math.max(
        0,
        Math.min(n - 1, Math.round(((e.clientX - r.left) / r.width) * (n - 1)))
      );
      const d = String((dates || [])[i] || '').slice(0, 10);
      el.dataset.scrub = 'on';
      el.style.setProperty('--sx', `${(i / (n - 1)) * 100}%`);
      el.textContent = d
        ? `${d.slice(8, 10)}/${d.slice(5, 7)} · ${axisLabel((daily || [])[i])} · Σ ${axisLabel(
            (values || [])[i]
          )}`
        : '';
    },
    [geo, values, daily, dates, axisLabel, readRef, hitRef]
  );

  const onLeave = useCallback(() => {
    const el = readRef?.current;
    if (el) {
      el.dataset.scrub = 'off';
      el.textContent = '';
    }
  }, [readRef]);

  if (!geo || geo.flat) return null;

  return (
    <>
      <svg
        className={styles.plot}
        viewBox={`0 0 ${geo.W} ${geo.H}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id={`pa${gid}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={from} stopOpacity="0.32" />
            <stop offset="55%" stopColor={from} stopOpacity="0.12" />
            <stop offset="100%" stopColor={from} stopOpacity="0.02" />
          </linearGradient>
          {/* userSpaceOnUse is required, not preferred: the curve is a thin
              path, and an objectBoundingBox gradient over a near-degenerate
              box paints unpredictably. */}
          <linearGradient
            id={`pl${gid}`}
            x1="0"
            y1="0"
            x2={geo.W}
            y2="0"
            gradientUnits="userSpaceOnUse"
          >
            <stop offset="0%" stopColor={from} />
            <stop offset="100%" stopColor={to} />
          </linearGradient>
        </defs>

        {geo.bands.map((b, i) => (
          <rect key={`b${i}`} className={styles.band} x={b.x} y="0" width={b.w} height={geo.H} />
        ))}

        {[0.25, 0.5, 0.75].map((f) => (
          <line
            key={f}
            className={styles.grid}
            x1="0"
            x2={geo.W}
            y1={geo.H * f}
            y2={geo.H * f}
            vectorEffect="non-scaling-stroke"
          />
        ))}

        {/* pathLength=1 lets the shared dshDraw keyframe grow each column off
            the floor with no second keyframe. Butt caps: a round cap leaves a
            visible dot sitting on the baseline before the column has grown. */}
        {geo.cols.map((c, i) =>
          c.h > 0 ? (
            <path
              key={`c${i}`}
              className={styles.col}
              d={`M${c.x.toFixed(1)},${geo.H} L${c.x.toFixed(1)},${(geo.H - c.h).toFixed(1)}`}
              stroke={from}
              pathLength="1"
              style={{ '--dsh-len': 1, '--d': `${0.2 + Math.min(i, 40) * 0.012}s` }}
              vectorEffect="non-scaling-stroke"
            />
          ) : null
        )}

        {geo.paceY !== null && (
          <line
            className={styles.pace}
            x1="0"
            x2={geo.W}
            y1={geo.paceY}
            y2={geo.paceY}
            vectorEffect="non-scaling-stroke"
          />
        )}

        <path d={geo.area} fill={`url(#pa${gid})`} className={styles.area} />
        {/* An offset twin rather than filter: drop-shadow — a CSS filter would
            re-rasterise on every frame of the draw-in. */}
        <path
          d={geo.line}
          className={styles.curveShadow}
          style={{ '--dsh-len': geo.len }}
          vectorEffect="non-scaling-stroke"
        />
        <path
          d={geo.line}
          className={styles.curve}
          stroke={`url(#pl${gid})`}
          style={{ '--dsh-len': geo.len }}
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {/* Crisp round marker as HTML: a circle inside a preserveAspectRatio
          "none" viewBox would be squashed into an ellipse. */}
      <i
        className={`${styles.tip} ${styles[`dot${tone}`]}`}
        style={{
          left: `${(geo.end[0] / geo.W) * 100}%`,
          top: `${(geo.end[1] / geo.H) * 100}%`,
        }}
        aria-hidden="true"
      />

      <span
        ref={hitRef}
        className={styles.hit}
        onPointerMove={onMove}
        onPointerLeave={onLeave}
        aria-hidden="true"
      />
    </>
  );
};

// A period that has produced nothing still gets a drawn instrument: the scale
// stays, one ghost stub per elapsed bucket sits on the floor, and the label
// says so plainly. Standby, not breakage.
const Standby = ({ count, tone, label }) => (
  <div className={styles.standby}>
    <span className={styles.standbyBars} aria-hidden="true">
      {Array.from({ length: Math.max(6, Math.min(count || 0, 31)) }).map((_, i) => (
        <i key={i} style={{ '--d': `${0.25 + i * 0.02}s` }} />
      ))}
    </span>
    <span className={styles.standbyLabel}>
      <i className={`${styles.standbyDot} ${styles[`dot${tone}`]}`} aria-hidden="true" />
      {label}
    </span>
  </div>
);

// ── The calibrated gauge ───────────────────────────────────────────────────
// A proportion drawn as a reading rather than a bar: a tick scale above, a
// filled track with a needle parked at its edge, and a two-swatch legend so
// both halves of the ratio are stated instead of only the flattering one.
const Gauge = ({ value, total, tone, caption, legend, progress }) => {
  const pct = total > 0 ? Math.max(0, Math.min(100, (value / total) * 100)) : 0;
  const live = pct * progress;

  return (
    <div className={styles.gauge}>
      <div className={styles.gaugeHead}>
        <span className={styles.ticks} aria-hidden="true" />
        <span className={`dshNum ${styles.gaugePct}`}>
          {total > 0 ? (
            <>
              {Math.round(live)}
              <em className={styles.gaugeUnit}>%</em>
            </>
          ) : (
            <em className={styles.gaugeUnit}>—</em>
          )}
          <em className={styles.gaugeCaption}>{caption}</em>
        </span>
      </div>

      <div className={styles.track}>
        <span className={`${styles.fill} ${styles[`fill${tone}`]}`} style={{ width: `${live}%` }}>
          <i className={styles.sheen} aria-hidden="true" />
        </span>
        {/* The needle rides a full-width inner track so its translateX resolves
            against that width and stays a compositor transform — animating
            `left` on the needle itself would be a layout animation. */}
        <span className={styles.needleTrack} aria-hidden="true">
          <i className={styles.needle} style={{ transform: `translateX(${live}%)` }} />
        </span>
      </div>

      {legend && (
        <div className={styles.legend}>
          {legend.map((l) => (
            <span key={l.label} className={styles.legendItem}>
              <i
                className={`${styles.swatch} ${l.dim ? styles.swatchDim : styles[`fill${tone}`]}`}
              />
              <em>{l.label}</em>
              <b className="dshNum">{l.value}</b>
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

const Cell = ({
  eyebrow,
  hint,
  live,
  stamp,
  icon: Icon,
  value,
  format,
  delta,
  reference,
  meta,
  visual,
  loading,
  progress,
  index,
}) => {
  const tone = visual?.tone || 'Accent';
  const readRef = useRef(null);
  const hitRef = useRef(null);
  const fig = splitFigure(format(value * progress));

  const renderDelta = () => {
    if (delta === null || delta === undefined) return null;

    // A period that simply has not produced yet reads as quiet, not as a
    // collapse — so a zero value gets a neutral dash, never "-100%".
    if (!value || delta === 0) {
      return (
        <span className={`dshPill dshPillMute ${styles.delta}`} title="No change to report yet">
          <Minus size={10} strokeWidth={3} />
        </span>
      );
    }
    const up = delta > 0;
    const Arrow = up ? ArrowUpRight : ArrowDownRight;
    return (
      <span
        className={`dshPill ${up ? 'dshPillPos' : 'dshPillNeg'} ${styles.delta}`}
        title={`${up ? 'Up' : 'Down'} ${Math.abs(delta)}% vs the previous period to the same point`}
      >
        <Arrow size={10} strokeWidth={3} />
        {Math.abs(delta)}%
      </span>
    );
  };

  const isPlot = visual?.kind === 'plot';
  const plotEmpty = isPlot && !(visual.values || []).some((n) => Number(n) > 0);

  return (
    <div className={`${styles.cell} ${styles[`cell${tone}`]}`} style={{ '--i': index }}>
      <span className={styles.rail} aria-hidden="true" />
      <span className={styles.railWide} aria-hidden="true" />
      <span className={styles.grain} aria-hidden="true" />

      <div className={styles.head} title={hint}>
        <span className={`${styles.chip} ${styles[`chip${tone}`]}`}>
          <Icon size={13} strokeWidth={2.3} />
        </span>
        <span className={styles.eyebrow}>{eyebrow}</span>
        {/* Two of the four tiles describe right now, not the selected period.
            Saying so beats letting a filtered dashboard imply otherwise. */}
        {live && <span className={styles.liveTag}>now</span>}
        {stamp && <span className={`dshNum ${styles.stamp}`}>{stamp}</span>}
      </div>

      {loading ? (
        <span className={`dshSkel ${styles.skelValue}`} />
      ) : (
        <div className={styles.figure}>
          {fig.pre && <span className={styles.figMark}>{fig.pre}</span>}
          <span className={`dshNum ${styles.figInt}`}>{fig.int}</span>
          {fig.dec && <span className={`dshNum ${styles.figDec}`}>,{fig.dec}</span>}
          {fig.post && <span className={styles.figMark}>{fig.post}</span>}
        </div>
      )}

      {loading ? (
        <span className={`dshSkel ${styles.skelRef}`} />
      ) : (
        <div className={styles.reference}>
          {renderDelta()}
          {reference && <span className={styles.refText}>{reference}</span>}
        </div>
      )}

      <div className={styles.bay}>
        {loading ? (
          <span className={`dshSkel ${styles.skelBay}`} />
        ) : isPlot ? (
          plotEmpty ? (
            <Standby count={(visual.values || []).length} tone={tone} label={visual.emptyLabel} />
          ) : (
            <>
              <div className={styles.bayHead}>
                <span className={`dshNum ${styles.axis}`}>{visual.axisTop}</span>
                <span ref={readRef} className={styles.readout} data-scrub="off" aria-hidden="true" />
              </div>
              <div className={styles.plotWrap}>
                <Plot
                  values={visual.values}
                  daily={visual.daily}
                  dates={visual.dates}
                  tone={tone}
                  pace={visual.pace}
                  axisLabel={visual.axisLabel}
                  readRef={readRef}
                  hitRef={hitRef}
                />
              </div>
            </>
          )
        ) : visual?.kind === 'gauge' ? (
          <Gauge
            value={visual.value}
            total={visual.total}
            tone={tone}
            caption={visual.caption}
            legend={visual.legend}
            progress={progress}
          />
        ) : null}
      </div>

      <div className={styles.shelf}>
        {loading ? (
          <span className={`dshSkel ${styles.skelMeta}`} />
        ) : (
          meta.map((m, i) => (
            <span key={m.label} className={styles.shelfItem}>
              {i > 0 && <i className={styles.shelfDot} />}
              <em className={styles.shelfLabel}>{m.label}</em>
              <b className={`dshNum ${styles.shelfValue}`}>{m.value}</b>
            </span>
          ))
        )}
      </div>
    </div>
  );
};

const KpiStrip = ({ data, loading, periodLabel }) => {
  const series = data?.revenueSeries;
  const points = series?.points || [];
  const period = periodLabel || 'the selected period';
  const prevLabel = data?.period?.previous?.label || 'the previous period';

  // The API is mid-migration from month-scoped to period-scoped figures. Read
  // the period fields first and fall back to the month ones, so this renders
  // correctly against either shape rather than showing zeros on whichever
  // backend it happens to meet. The extras a period payload adds — the window
  // stamp and the previous-period comparators — are each guarded below and
  // simply do not draw when the older shape is served.
  const bookingsValue = data?.bookings?.period ?? data?.bookings?.month ?? 0;
  const bookingsDelta = data?.bookings?.deltaPeriod ?? data?.bookings?.deltaMonth;
  const revenueValue = data?.revenue?.period ?? data?.revenue?.month ?? 0;
  const revenueDelta = data?.revenue?.deltaPeriod ?? data?.revenue?.deltaMonth;
  const newCustomers = data?.customers?.newInPeriod ?? data?.customers?.newThisWeek ?? 0;
  const newCustomersLabel =
    data?.customers?.newInPeriod === undefined ? 'New this week' : 'New in period';

  const animKey = useMemo(
    () =>
      JSON.stringify([
        data?.bookings?.period ?? data?.bookings?.month,
        data?.revenue?.period ?? data?.revenue?.month,
        data?.customers?.active,
        data?.pendingPayments?.totalOutstanding,
      ]),
    [data]
  );
  const progress = useCountUp(!loading, animKey);

  const collected = data?.finance?.totalCollected ?? 0;
  const outstanding = data?.pendingPayments?.totalOutstanding ?? 0;
  const billed = collected + outstanding;
  const active = data?.customers?.active ?? 0;
  const repeat = data?.people?.repeatCustomers ?? 0;

  const dates = points.map((p) => p.date);
  const dailyBookings = points.map((p) => p.bookings);
  const dailyRevenue = points.map((p) => p.revenue);

  // Revenue already arrives cumulative; bookings are per-bucket, so the running
  // total is summed here to keep both curves on the same contract — the last
  // point equals the headline figure.
  const cumBookings = useMemo(() => {
    let run = 0;
    return dailyBookings.map((n) => (run += Number(n) || 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(dailyBookings)]);
  const cumRevenue = points.map((p) => p.cumulative);

  // How far into the measured period the data runs — "D 12/31".
  const stamp =
    series?.elapsed && series?.span
      ? `${String(series.unit || 'Day').charAt(0)} ${series.elapsed}/${series.span}`
      : null;

  // Same migration as above: the previous-period absolutes are what the pace
  // line and the comparator sentences are drawn from, and they are served under
  // both names. Missing on either shape simply means neither is drawn.
  const prevBookings = data?.bookings?.prevPeriod ?? data?.revenue?.prevPeriodBookings ?? data?.revenue?.prevMonthBookings ?? 0;
  const prevRevenue = data?.revenue?.prevPeriod ?? data?.revenue?.prevMonthToDate ?? 0;

  // Nothing outstanding is a good outcome, not a warning: the fourth tile flips
  // its tone and icon rather than glowing amber at zero.
  const settled = outstanding <= 0 && collected > 0;

  const cells = [
    {
      eyebrow: 'Bookings',
      hint: `Bookings created in ${period}`,
      stamp,
      icon: TicketCheck,
      value: bookingsValue,
      format: fmtInt,
      delta: bookingsDelta,
      reference:
        prevBookings > 0 ? `vs ${fmtInt(prevBookings)} by this point in ${prevLabel}` : null,
      visual: {
        kind: 'plot',
        tone: 'Brand',
        values: cumBookings,
        daily: dailyBookings,
        dates,
        pace: prevBookings,
        axisTop: fmtInt(Math.max(...cumBookings, prevBookings, 0)),
        axisLabel: (n) => fmtInt(n || 0),
        emptyLabel: `No bookings in ${period} yet`,
      },
      meta: [
        { label: 'Today', value: fmtInt(data?.bookings?.today ?? 0) },
        { label: 'This week', value: fmtInt(data?.bookings?.week ?? 0) },
      ],
    },
    {
      eyebrow: 'Revenue',
      hint: `Revenue booked in ${period}`,
      stamp,
      icon: Banknote,
      value: revenueValue,
      format: fmtEUR,
      delta: revenueDelta,
      reference:
        prevRevenue > 0 ? `vs ${fmtEUR(prevRevenue)} by this point in ${prevLabel}` : null,
      visual: {
        kind: 'plot',
        tone: 'Pos',
        values: cumRevenue,
        daily: dailyRevenue,
        dates,
        pace: prevRevenue,
        axisTop: fmtEUR(Math.max(...cumRevenue.map((n) => n || 0), prevRevenue, 0)),
        axisLabel: (n) => fmtEUR(n || 0),
        emptyLabel: `No revenue in ${period} yet`,
      },
      meta: [
        { label: 'Today', value: fmtEUR(data?.revenue?.today ?? 0) },
        { label: 'Year', value: fmtEUR(data?.revenue?.year ?? 0) },
      ],
    },
    {
      eyebrow: 'Active customers',
      hint: 'Currently active customers — not affected by the period filter',
      live: true,
      icon: Users,
      value: active,
      format: fmtInt,
      delta: undefined,
      reference: repeat > 0 ? `${fmtInt(repeat)} have booked more than once` : null,
      visual: {
        kind: 'gauge',
        tone: 'Accent',
        caption: 'repeat',
        value: repeat,
        total: active,
        legend: [
          { label: 'Repeat', value: fmtInt(repeat) },
          { label: 'First-time', value: fmtInt(Math.max(0, active - repeat)), dim: true },
        ],
      },
      meta: [
        { label: newCustomersLabel, value: `+${fmtInt(newCustomers)}` },
        { label: 'Repeat', value: fmtInt(repeat) },
      ],
    },
    {
      eyebrow: settled ? 'All invoices collected' : 'Awaiting collection',
      hint: 'Outstanding client balance right now — not affected by the period filter',
      live: true,
      icon: settled ? CheckCircle2 : Hourglass,
      value: outstanding,
      format: fmtEUR,
      delta: undefined,
      reference:
        billed > 0
          ? `${fmtEUR(billed)} billed · ${fmtInt(data?.pendingPayments?.count ?? 0)} bookings open`
          : null,
      visual: {
        kind: 'gauge',
        tone: settled ? 'Pos' : 'Warn',
        caption: settled ? 'collected' : 'of billed',
        value: settled ? collected : outstanding,
        total: billed,
        legend: [
          { label: 'Outstanding', value: fmtEUR(outstanding) },
          { label: 'Collected', value: fmtEUR(collected), dim: true },
        ],
      },
      meta: [
        { label: 'Bookings', value: fmtInt(data?.pendingPayments?.count ?? 0) },
        { label: 'Collected', value: fmtEUR(collected) },
      ],
    },
  ];

  return (
    <div className={`dshPanel ${styles.strip}`}>
      {cells.map((c, i) => (
        <Cell key={c.eyebrow} {...c} index={i} loading={loading} progress={progress} />
      ))}
    </div>
  );
};

export default KpiStrip;
